import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

function ProgressRing({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-2" data-testid="traeger-preview-progress">
      <div className="relative w-[88px] h-[88px]">
        <svg width="88" height="88" viewBox="0 0 88 88" className="-rotate-90">
          <circle cx="44" cy="44" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
          <circle cx="44" cy="44" r={r} fill="none" stroke="#0a788c" strokeWidth="7" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: "stroke-dashoffset 200ms linear" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-lg font-semibold text-slate-800 tabular-nums" data-testid="traeger-preview-progress-pct">{pct} %</div>
      </div>
      <div className="text-xs text-slate-500">Vorschau wird erstellt – bitte kurz warten …</div>
    </div>
  );
}

function useFakeProgress(active) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    if (!active) { setPct(0); return undefined; }
    const start = Date.now();
    const t = setInterval(() => setPct(Math.min(95, Math.round(100 * (1 - Math.exp(-(Date.now() - start) / 2500))))), 150);
    return () => clearInterval(t);
  }, [active]);
  return pct;
}

export default function TraegerLivePreview({ formId, fields, pagesKey = "" }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const urlRef = useRef(null);
  const pct = useFakeProgress(busy);
  const key = JSON.stringify(fields.map((f) => [f.fid, f.value || "", f.rect]));

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true); setError(null);
      try {
        const r = await api.post(`/traeger-forms/form/${formId}/preview`, { fields: fields.map(({ fid, value }) => ({ fid, value: value || "" })) }, { responseType: "blob", timeout: 90000 });
        if (cancelled) return;
        const next = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = next;
        setUrl(next);
      } catch (e) {
        if (cancelled) return;
        let detail = "";
        try {
          const d = e?.response?.data;
          if (d instanceof Blob) detail = JSON.parse(await d.text())?.detail || "";
          else detail = d?.detail || "";
        } catch { /* kein JSON-Body */ }
        setError(
          e?.response?.status === 403 ? "Keine Berechtigung für die Vorschau."
            : detail ? `Vorschau konnte nicht erstellt werden: ${detail}`
            : "Vorschau konnte nicht erstellt werden."
        );
      } finally { if (!cancelled) setBusy(false); }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formId, key, pagesKey, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => urlRef.current && URL.revokeObjectURL(urlRef.current), []);

  return (
    <div className="lg:sticky lg:top-4 space-y-2" data-testid="traeger-live-preview">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="font-bold uppercase tracking-overline text-[10px]">Live-Vorschau</span>
        {busy && <span className="tabular-nums" data-testid="traeger-preview-busy">aktualisiert … {pct} %</span>}
      </div>
      {error && !busy && (
        <div className="border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-center gap-3" data-testid="traeger-preview-error">
          {error}
          <button onClick={() => setRetry((n) => n + 1)} className="inline-flex items-center gap-1 border border-rose-300 bg-white px-3 py-1 text-xs hover:bg-rose-100 transition-colors" data-testid="traeger-preview-retry"><RefreshCw size={12} /> Erneut versuchen</button>
        </div>
      )}
      {url ? (
        <div className="relative">
          <iframe title="Live-Vorschau" src={url} className={`w-full h-[75vh] border border-slate-200 bg-slate-50 transition-opacity ${busy ? "opacity-30" : "opacity-100"}`} data-testid="traeger-live-preview-frame" />
          {busy && <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px]"><ProgressRing pct={pct} /></div>}
          <a href={url} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 text-slate-600" data-testid="traeger-live-preview-open">In neuem Tab öffnen</a>
        </div>
      ) : (
        <div className="h-[40vh] border border-dashed border-slate-300 flex items-center justify-center">
          {busy ? <ProgressRing pct={pct} /> : !error && <span className="text-sm text-slate-400">Vorschau wird vorbereitet …</span>}
        </div>
      )}
    </div>
  );
}
